/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework from '@greenville/framework';
import { comparePromptVersionStrings, resolvePromptVersionLabel } from '../../../common/prompt/catalogueUtils';
import { getSystemPromptFromDocument, getUserPromptFromDocument } from './promptApiMappers';
import * as constants from '../constants/ServiceConstants';

const clonePromptMessages = (messages) => (Array.isArray(messages) ? messages.map((message) => ({ ...message })) : []);

export const mergePromptMessagesFromForm = (existingStart, existingEnd, systemPrompt, userPrompt) => {
  let promptStart = clonePromptMessages(existingStart);
  let promptEnd = clonePromptMessages(existingEnd);

  if (promptStart.length === 0) {
    promptStart = [{ role: 'system', content: systemPrompt ?? '' }];
  } else {
    const systemIndex = promptStart.findIndex((message) => message?.role === 'system');
    if (systemIndex >= 0) {
      promptStart[systemIndex] = { ...promptStart[systemIndex], content: systemPrompt ?? '' };
    } else {
      promptStart = [{ role: 'system', content: systemPrompt ?? '' }, ...promptStart];
    }
  }

  if (promptEnd.length === 0) {
    promptEnd = [{ role: 'user', content: userPrompt ?? '' }];
  } else {
    const userIndex = promptEnd.findIndex((message) => message?.role === 'user');
    if (userIndex >= 0) {
      promptEnd[userIndex] = { ...promptEnd[userIndex], content: userPrompt ?? '' };
    } else {
      promptEnd = [...promptEnd, { role: 'user', content: userPrompt ?? '' }];
    }
  }

  return { promptStart, promptEnd };
};

export const buildUpdatePromptPayload = ({
  sourceVersion,
  systemPrompt,
  userPrompt,
  promptStart: existingStart,
  promptEnd: existingEnd,
  name,
  description,
  comments,
  isAgentic = true
}) => {
  const { promptStart, promptEnd } = mergePromptMessagesFromForm(existingStart, existingEnd, systemPrompt, userPrompt);
  return {
    version: sourceVersion,
    name: name ?? '',
    description: description ?? '',
    comments: comments ?? '',
    promptStart,
    promptEnd,
    isAgentic
  };
};

export const buildFullPromptUpdatePayload = ({
  sourceVersion,
  name,
  description,
  comments,
  promptStart,
  promptEnd,
  isAgentic = true
}) => ({
  version: sourceVersion,
  name: name ?? '',
  description: description ?? '',
  comments: comments ?? '',
  promptStart: Array.isArray(promptStart) ? promptStart : [],
  promptEnd: Array.isArray(promptEnd) ? promptEnd : [],
  isAgentic
});

export const buildCreatePromptPayload = ({
  name,
  description,
  comments,
  promptStart,
  promptEnd,
  isAgentic = true
}) => ({
  name,
  description: description ?? '',
  comments: comments ?? '',
  promptStart: Array.isArray(promptStart) && promptStart.length > 0 ? promptStart : [{ role: 'system', content: '' }],
  promptEnd: Array.isArray(promptEnd) && promptEnd.length > 0 ? promptEnd : [{ role: 'user', content: '' }],
  isAgentic
});

export const formatPromptVersionOptions = (versions) => {
  if (!Array.isArray(versions)) {
    return [];
  }
  const options = versions
    .map((row) => {
      const version = resolvePromptVersionLabel(row);
      if (!version) {
        return null;
      }
      return {
        value: String(version),
        label: String(version)
      };
    })
    .filter(Boolean);

  return options.sort((a, b) => comparePromptVersionStrings(b.value, a.value));
};

export const promptDocumentMatchesForm = (doc, systemPrompt, userPrompt) => {
  if (!doc) {
    return false;
  }
  return (
    getSystemPromptFromDocument(doc) === (systemPrompt ?? '') && getUserPromptFromDocument(doc) === (userPrompt ?? '')
  );
};

export const waitForPromptVersionHydration = (promptRef, promptVersionRef) =>
  new Promise((resolve) => {
    let unsubOk;
    let unsubErr;
    const cleanup = () => {
      if (typeof unsubOk === 'function') unsubOk();
      if (typeof unsubErr === 'function') unsubErr();
    };
    const matchesRequest = (event) => {
      if (promptRef && event?.promptRef && event.promptRef !== promptRef) {
        return false;
      }
      if (promptVersionRef && event?.promptVersionRef && event.promptVersionRef !== promptVersionRef) {
        return false;
      }
      return true;
    };
    unsubOk = Framework.getEventManager().on(constants.GET_PROMPT_VERSION_SUCCESS, (event) => {
      if (!matchesRequest(event)) {
        return;
      }
      cleanup();
      resolve(true);
    });
    unsubErr = Framework.getEventManager().on(constants.GET_PROMPT_VERSION_ERROR, (event) => {
      if (!matchesRequest(event)) {
        return;
      }
      cleanup();
      resolve(false);
    });
  });
